import { useTranslation } from "react-i18next";
import { useCallback, useDeferredValue, useMemo } from "react";
import type { DashboardManual } from "@arc/contracts";
import { ratingsEnabledFor } from "@arc/tenant-config";
import { Button } from "@/components/ui/button";
import type { AuthSession } from "../../../auth/keycloak.js";
import { useFacilities } from "../../facilities/useFacilities.js";
import { useFacilityNavParams } from "../../facilities/drilldown/useFacilityNavParams.js";
import { facilityPathLabel } from "../../facilities/facility-tree.js";
import { useDashboardManuals } from "../useDashboardManuals.js";
import { ManualListToolbar } from "./ManualListToolbar.js";
import { ManualListRows } from "./ManualListRows.js";
import { RatingBars } from "./RatingBars.js";
import { filterManuals, groupByFacility, ratingDistribution, sortManuals } from "./manual-list-model.js";
import { useDashboardListStore, type ListPage } from "./useDashboardListStore.js";

/** The empty-state message KEY per page — module-level, so it cannot call a hook. */
const EMPTY_KEY = {
  manuals: "dashboard.noManuals",
  favourites: "dashboard.noFavouriteManualsYetStarA",
  ratings: "dashboard.noManuals",
} as const satisfies Record<ListPage, string>;

/**
 * ONE list engine, three pages (ADR-034 D2): Manuals (every visible manual, the archived switch), Favourite manuals
 * (the list filtered to the caller's favourites) and Ratings (the same list with the star column + the distribution
 * chart). Sort / filter / group are client-side over `GET /dashboard/manuals`; the list state lives in the store so
 * it survives the details-drawer round-trip (D3). A row opens the manual's details in its facility.
 */
export function ManualListPage({ session, mode }: { session: AuthSession; mode: ListPage }) {
  const { t } = useTranslation();
  const nav = useFacilityNavParams();
  const ratingsEnabled = ratingsEnabledFor(session.realm);
  const state = useDashboardListStore((s) => s.pages[mode]);
  const patch = useDashboardListStore((s) => s.patch);
  const setFilters = useDashboardListStore((s) => s.setFilters);
  const resetFilters = useDashboardListStore((s) => s.resetFilters);
  const manuals = useDashboardManuals(session, mode === "manuals" && state.archived);
  const facilities = useFacilities(session);

  // Facility path labels from the caller's tree ("Site / Line / Machine" — the repo's breadcrumb separator); a facility
  // not in the tree (an archived one the tree omits) falls back to the row's own facility name.
  const byId = useMemo(() => new Map((facilities.data ?? []).map((f) => [f.id, { name: f.name, parentId: f.parentId }] as const)), [facilities.data]);
  const labelOf = useCallback(
    (m: DashboardManual) => {
      const node = byId.get(m.facilityId);
      if (!node) return m.facilityName;
      const ancestors = facilityPathLabel(byId, m.facilityId);
      return ancestors ? `${ancestors} / ${node.name}` : node.name;
    },
    [byId],
  );

  const base = useMemo(() => {
    const all = manuals.data ?? [];
    return mode === "favourites" ? all.filter((m) => m.favouritedAt !== null) : all;
  }, [manuals.data, mode]);
  // The WHOLE filter record is deferred (the store hands a new object only when something changed): the input echoes
  // a keystroke at once, the list follows when React is idle — a memo on the live record would recompute every
  // keystroke and defeat the deferral (diff panel).
  const deferredFilters = useDeferredValue(state.filters);
  const filtered = useMemo(() => filterManuals(base, deferredFilters, labelOf), [base, deferredFilters, labelOf]);
  const sorted = useMemo(() => sortManuals(filtered, state.sort, state.reverse), [filtered, state.sort, state.reverse]);
  const groups = useMemo(() => (state.grouped ? groupByFacility(sorted, labelOf) : null), [state.grouped, sorted, labelOf]);
  // The histogram spans every OTHER filter but not the rating band itself — clicking a bar narrows the rows, never
  // the chart (diff panel: a faceted chart that collapses to its own selection is misleading).
  const distribution = useMemo(
    () => (mode === "ratings" ? ratingDistribution(filterManuals(base, { ...deferredFilters, rating: 0 }, labelOf)) : null),
    [mode, base, deferredFilters, labelOf],
  );

  const open = (m: DashboardManual) => nav.openManualDetails(m.facilityId, m.id);

  return (
    <div className="flex flex-col gap-4">
      <ManualListToolbar
        mode={mode}
        state={state}
        shown={filtered.length}
        total={base.length}
        onPatch={(p) => patch(mode, p)}
        onFilters={(p) => setFilters(mode, p)}
        onReset={() => resetFilters(mode)}
        ratingsEnabled={ratingsEnabled}
      />
      {facilities.isError ? (
        <p role="alert" className="text-attention flex flex-wrap items-center gap-2 text-xs">
          {t("dashboard.couldntLoadTheFacilityTreeRows")}
          <Button variant="outline" size="sm" className="h-6 px-2 text-xs" onPress={() => void facilities.refetch()}>
            {t("dashboard.tryAgain")}
          </Button>
        </p>
      ) : null}
      {distribution !== null && ratingsEnabled ? (
        <div className="max-w-md">
          <RatingBars distribution={distribution} selected={state.filters.rating} onSelect={(r) => setFilters(mode, { rating: state.filters.rating === r ? 0 : r })} />
        </div>
      ) : null}
      {manuals.isPending ? (
        <p role="status" className="text-muted-foreground text-sm">
          {t("other.loading")}
        </p>
      ) : manuals.isError ? (
        <div className="flex flex-col items-start gap-2">
          <p role="alert" className="text-destructive text-sm">
            {t("dashboard.couldntLoadTheManuals")}
          </p>
          <Button variant="outline" size="sm" onPress={() => void manuals.refetch()}>
            {t("dashboard.tryAgain")}
          </Button>
        </div>
      ) : base.length === 0 ? (
        <p className="text-muted-foreground text-sm">{state.archived && mode === "manuals" ? t("dashboard.noArchivedManuals") : t(EMPTY_KEY[mode])}</p>
      ) : filtered.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t("dashboard.noManualsMatchFilters")}</p>
      ) : (
        <ManualListRows mode={mode} sort={state.sort} groups={groups} flat={sorted} labelOf={labelOf} onOpen={open} />
      )}
    </div>
  );
}
